import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LazyLoadEvent } from 'primeng/api/lazyloadevent';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';
import { FilterValues } from 'src/app/shared/components/filter/filter.component';
import { EstampApiService } from 'src/app/core/services/estamp-api.service';
import { EstampApiKeyService } from 'src/app/core/services/estamp-api-key.service';

@Component({
  selector: 'app-customer-order-list',
  templateUrl: './customer-order-list.component.html',
  styleUrl: './customer-order-list.component.scss'
})
export class CustomerOrderListComponent implements OnInit, OnDestroy {

  loading = false;
  orders: any[] = [];
  orderDetail: any = {};

  showModal: boolean = false;
  @ViewChild('dt') table!: Table;

  private subscriptions = new Subscription();

  constructor(
    private estampApi: EstampApiService,
    private apiKeyService: EstampApiKeyService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    // Reload the list as soon as a demo API key becomes available.
    this.subscriptions.add(
      this.apiKeyService.changes.subscribe(() => {
        if (this.apiKeyService.hasApiKey && this.table) {
          this.table.reset();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Row key: the demo API identifies orders by `_id`. */
  getOrderKey(order: any): string {
    return order?._id || order?.orderId || order?.id || '';
  }

  /** Collection request 3: GET eSTAMP ORDERS LIST (page & limit only) */
  getStampOrders(event: LazyLoadEvent) {
    if (!this.apiKeyService.hasApiKey) {
      this.orders = [];
      this.totalRecords = 0;
      return;
    }

    Promise.resolve().then(() => this.loading = true);
    const first = event?.first ?? 0;
    const rows = event?.rows ?? this.rows;
    const page = first / rows + 1;

    this.estampApi.getOrders(page, rows).subscribe(
      (res: any) => {
        const orders = this.extractOrders(res);

        this.orders = this.applyFilters(orders);
        this.totalRecords = this.extractTotalCount(res, orders.length);
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  /** Collection request 5: GET eSTAMP ORDER DETAILS */
  viewOrder(ord: any) {
    const id = this.getOrderKey(ord);
    if (!id) {
      return;
    }

    this.estampApi.getOrderDetail(id).subscribe(
      (res: any) => {
        this.showModal = true;
        this.orderDetail = res?.data || res;
      },
      () => { }
    );
  }

  closeModal() {
    this.showModal = false;
  }

  navigateStampOrderCreate = () => {
    this.router.navigate(['/customer/estamp/create-customer-order']);
  }


  totalRecords = 0;
  rows = 10;
  searchText: any = '';
  rangeDates: Date[] | null = null;

  onFilterChange(values: FilterValues) {
    this.searchText = values.search;
    this.rangeDates = values.dateRange;
    this.table.reset();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.searchText ||
      (this.rangeDates && this.rangeDates.length === 2)
    );
  }

  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private extractOrders(res: any): any[] {
    const orders =
      res?.data?.orders ??
      res?.data ??
      res?.orders ??
      res?.items ??
      res;

    return Array.isArray(orders) ? orders : [];
  }

  private extractTotalCount(res: any, fallback: number): number {
    return (
      res?.meta?.total ??
      res?.totalCount ??
      res?.total ??
      res?.pagination?.total ??
      fallback
    );
  }

  /**
   * The demo API paginates with `page` & `limit` only, so search / date
   * filters are applied to the rows of the current page.
   */
  private applyFilters(orders: any[]): any[] {
    if (!this.hasActiveFilters) {
      return orders;
    }

    const search = String(this.searchText ?? '').trim().toLowerCase();

    return orders.filter(order => {
      if (search) {
        const haystack = [
          order?.orderId,
          order?.denomination,
          order?.purpose,
          order?.clientEmail
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (this.rangeDates && this.rangeDates.length === 2 && order?.createdAt) {
        const createdAt = new Date(order.createdAt).getTime();
        const from = this.startOfDay(this.rangeDates[0]).getTime();
        const to = this.endOfDay(this.rangeDates[1]).getTime();

        if (isNaN(createdAt) || createdAt < from || createdAt > to) {
          return false;
        }
      }

      return true;
    });
  }

  private startOfDay(date: Date): Date {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
  }

  private endOfDay(date: Date): Date {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  }

}
